import React from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Material Kit 2 PRO React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import bgImage from "assets/images/content-5.jpg";

//other
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

function ReviewItem({ label, value, highlight }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;

  return (
    <MKBox display="flex" justifyContent="space-between" mb={1}>
      <MKTypography variant="body2" color="text.secondary">
        {label}
      </MKTypography>

      <MKTypography
        variant="body2"
        fontWeight={highlight ? 600 : 500}
        color={highlight ? "#700808" : "text.primary"}
        textAlign="right"
        sx={{ maxWidth: "60%" }}
      >
        {Array.isArray(value) ? value.join(", ") : value}
      </MKTypography>
    </MKBox>
  );
}

function ApplicantInfoStep({ formData, setFormData, errors = {} }) {
  const handleApplicant = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      applicant: { ...prev.applicant, [field]: e.target.value },
    }));
  };

  return (
    <>
      <MKBox mb={4}>
        {/* Section Title */}
        <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MKTypography variant="body2" mb={2} color="text.secondary">
            Tell us who will be renting the house — we’ll use these details to prepare your booking
            and contact you for confirmation.
          </MKTypography>
          <MKTypography
            component={Link}
            to="/services/booking"
            variant="body2"
            mb={2}
            fontWeight="medium"
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              textDecorationColor: "#700808",
              color: "#700808",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
                textDecorationColor: "#700808",
              },
            }}
          >
            All services
          </MKTypography>
        </MKBox>
        <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are
          required
        </MKTypography>
        {/* Modern Separator */}
        <MKBox
          sx={{
            height: "2px",
            width: "100%",
            background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
            borderRadius: "4px",
            mb: 3,
          }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MKInput
              label="Full Name"
              value={formData.applicant.fullName}
              onChange={handleApplicant("fullName")}
              fullWidth
              required
              error={Boolean(errors.fullName)}
              helperText={errors.fullName}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MKInput
              label="Phone Number"
              value={formData.applicant.phone}
              onChange={handleApplicant("phone")}
              fullWidth
              required
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MKInput
              label="Email Address"
              value={formData.applicant.email}
              onChange={handleApplicant("email")}
              fullWidth
              required
              error={Boolean(errors.email)}
              helperText={errors.email}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Preferred Contact Method</FormLabel>
            <RadioGroup
              row
              value={formData.applicant.contactMethod}
              onChange={handleApplicant("contactMethod")}
            >
              <FormControlLabel value="Phone" control={<Radio />} label="Phone" />
              <FormControlLabel value="Email" control={<Radio />} label="Email" />
              <FormControlLabel value="WhatsApp" control={<Radio />} label="WhatsApp" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12}>
            <MKInput
              label="Current Address"
              value={formData.applicant.address}
              onChange={handleApplicant("address")}
              fullWidth
              required
              error={Boolean(errors.address)}
              helperText={errors.address}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>
        </Grid>
      </MKBox>
    </>
  );
}

function RentalPreferencesStep({ formData, setFormData, errors = {} }) {
  const handleRental = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      rental: { ...prev.rental, [field]: e.target.value },
    }));
  };

  const handleRentalCheckbox = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      rental: { ...prev.rental, [field]: e.target.checked },
    }));
  };

  return (
    <>
      <MKBox mb={4}>
        {/* Section Title */}
        <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MKTypography variant="body2" mb={2} color="text.secondary">
            Share your housing preferences so we can find the best property match for you.
          </MKTypography>
          <MKTypography
            component={Link}
            to="/services/booking"
            variant="body2"
            mb={2}
            fontWeight="medium"
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              textDecorationColor: "#700808",
              color: "#700808",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
                textDecorationColor: "#700808",
              },
            }}
          >
            All services
          </MKTypography>
        </MKBox>
        <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are
          required
        </MKTypography>
        {/* Modern Separator */}
        <MKBox
          sx={{
            height: "2px",
            width: "100%",
            background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
            borderRadius: "4px",
            mb: 3,
          }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormLabel>Property Location</FormLabel>
            <MKInput
              label="Preferred Location"
              value={formData.rental.location}
              onChange={handleRental("location")}
              fullWidth
              required
              error={Boolean(errors.location)}
              helperText={errors.location}
              FormHelperTextProps={{ sx: { color: "#700808" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Property Type</FormLabel>
            <RadioGroup
              row
              value={formData.rental.propertyType}
              onChange={handleRental("propertyType")}
            >
              <FormControlLabel value="Apartment" control={<Radio />} label="Apartment" />
              <FormControlLabel value="House" control={<Radio />} label="House" />
              <FormControlLabel value="Studio" control={<Radio />} label="Studio" />
              <FormControlLabel value="Other" control={<Radio />} label="Other" />
            </RadioGroup>
            {errors.propertyType && (
              <MKTypography variant="caption" sx={{ color: "#700808" }}>
                {errors.propertyType}
              </MKTypography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Bedrooms</FormLabel>
            <RadioGroup row value={formData.rental.bedrooms} onChange={handleRental("bedrooms")}>
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3" control={<Radio />} label="3" />
              <FormControlLabel value="4+" control={<Radio />} label="4+" />
            </RadioGroup>
            {errors.bedrooms && (
              <MKTypography variant="caption" sx={{ color: "#700808" }}>
                {errors.bedrooms}
              </MKTypography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Bathrooms</FormLabel>
            <RadioGroup row value={formData.rental.bathrooms} onChange={handleRental("bathrooms")}>
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3+" control={<Radio />} label="3+" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Furnished</FormLabel>
            <RadioGroup row value={formData.rental.furnished} onChange={handleRental("furnished")}>
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
              <FormControlLabel value="Either" control={<Radio />} label="Either" />
            </RadioGroup>
            {errors.furnished && (
              <MKTypography variant="caption" sx={{ color: "#700808" }}>
                {errors.furnished}
              </MKTypography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Monthly Budget (RWF)*</FormLabel>
            <MKInput
              label=""
              value={formData.rental.budget}
              onChange={handleRental("budget")}
              fullWidth
              required
              error={Boolean(errors.budget)}
              helperText={errors.budget}
              FormHelperTextProps={{ sx: { color: "#700808" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Preferred Move-in Date*</FormLabel>
            <MKInput
              type="date"
              label=""
              value={formData.rental.moveInDate}
              onChange={handleRental("moveInDate")}
              fullWidth
              required
              error={Boolean(errors.moveInDate)}
              helperText={errors.moveInDate}
              FormHelperTextProps={{ sx: { color: "#700808" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.rental.parking}
                  onChange={handleRentalCheckbox("parking")}
                />
              }
              label="Parking Needed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.rental.petFriendly}
                  onChange={handleRentalCheckbox("petFriendly")}
                />
              }
              label="Pet Friendly"
            />
          </Grid>
        </Grid>
      </MKBox>
    </>
  );
}
function RentalRequirementsStep({ formData, setFormData, errors = {} }) {
  const handleRequirements = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      requirements: { ...prev.requirements, [field]: e.target.checked },
    }));
  };

  return (
    <>
      <MKBox mb={4}>
        {/* Section Title */}
        <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MKTypography variant="body2" mb={2} color="text.secondary">
            Select your preferred property features and share any additional requirements for your
            rental.
          </MKTypography>
          <MKTypography
            component={Link}
            to="/services/booking"
            variant="body2"
            mb={2}
            fontWeight="medium"
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              textDecorationColor: "#700808",
              color: "#700808",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
                textDecorationColor: "#700808",
              },
            }}
          >
            All services
          </MKTypography>
        </MKBox>
        <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are
          required
        </MKTypography>
        {/* Modern Separator */}
        <MKBox
          sx={{
            height: "2px",
            width: "100%",
            background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
            borderRadius: "4px",
            mb: 3,
          }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MKTypography fontWeight={600}>Additional Requirements</MKTypography>
          </Grid>

          {[
            ["privateCompound", "Private Compound"],
            ["security", "Security Services"],
            ["internet", "Internet Included"],
            ["garden", "Garden / Outdoor Space"],
            ["transport", "Near Public Transport"],
          ].map(([key, label]) => (
            <Grid item xs={12} md={6} key={key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requirements[key]}
                    onChange={handleRequirements(key)}
                  />
                }
                label={label}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <MKTypography fontWeight={600} mb={1}>
              Additional Notes*
            </MKTypography>
            <MKInput
              label="Other Requirements"
              value={formData.requirements.other}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  requirements: { ...p.requirements, other: e.target.value },
                }))
              }
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors.other)}
              helperText={errors.other}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>
        </Grid>
      </MKBox>
    </>
  );
}
function RentalDurationStep({ formData, setFormData, errors = {} }) {
  return (
    <>
      <MKBox mb={4}>
        {/* Section Title */}
        <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MKTypography variant="body2" mb={2} color="text.secondary">
            Tell us how long you plan to rent and any specific needs we should consider.
          </MKTypography>
          <MKTypography
            component={Link}
            to="/services/booking"
            variant="body2"
            mb={2}
            fontWeight="medium"
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              textDecorationColor: "#700808",
              color: "#700808",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
                textDecorationColor: "#700808",
              },
            }}
          >
            All services
          </MKTypography>
        </MKBox>
        <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are
          required
        </MKTypography>
        {/* Modern Separator */}
        <MKBox
          sx={{
            height: "2px",
            width: "100%",
            background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
            borderRadius: "4px",
            mb: 3,
          }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MKTypography fontWeight={600}>Rental Duration *</MKTypography>

            <RadioGroup
              value={formData.duration.duration}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  duration: { ...p.duration, duration: e.target.value },
                }))
              }
            >
              <FormControlLabel
                value="Short-term"
                control={<Radio />}
                label="Short-term (less than 6 months)"
              />
              <FormControlLabel
                value="Medium-term"
                control={<Radio />}
                label="Medium-term (6–12 months)"
              />
              <FormControlLabel
                value="Long-term"
                control={<Radio />}
                label="Long-term (1 year or more)"
              />
            </RadioGroup>

            {errors.duration && (
              <MKTypography variant="caption" sx={{ color: "#700808" }}>
                {errors.duration}
              </MKTypography>
            )}
          </Grid>

          <Grid item xs={12}>
            <MKTypography fontWeight={600} mb={1}>
              Additional Notes *
            </MKTypography>

            <MKInput
              label="Other Requirements"
              value={formData.duration.other}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  duration: { ...p.duration, other: e.target.value },
                }))
              }
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors.other)}
              helperText={errors.other}
              FormHelperTextProps={{ sx: { color: "#700808" } }}
            />
          </Grid>
        </Grid>
      </MKBox>
    </>
  );
}

function HouseRentalReviewSubmitStep({ formData, setFormData }) {
  const reviewCardStyle = {
    p: 3,
    borderRadius: "1.5rem",
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.03)",
  };

  return (
    <MKBox mb={4}>
      {/* Header */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" color="text.secondary">
          Review your rental request before submitting.
        </MKTypography>

        <MKTypography
          component={Link}
          to="/booking"
          variant="body2"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            color: "#700808",
            cursor: "pointer",
          }}
        >
          Other services
        </MKTypography>
      </MKBox>

      {/* Separator */}
      <MKBox
        sx={{
          height: 2,
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.25), transparent)",
          borderRadius: 2,
          mb: 3,
        }}
      />

      <Grid container spacing={3}>
        {/* Applicant Info */}
        <Grid item xs={12} md={6}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Applicant Information
            </MKTypography>

            <ReviewItem label="Full Name" value={formData.applicant?.fullName} />
            <ReviewItem label="Phone" value={formData.applicant?.phone} />
            <ReviewItem label="Email" value={formData.applicant?.email} />
            <ReviewItem label="Preferred Contact" value={formData.applicant?.contactMethod} />
            <ReviewItem label="Address" value={formData.applicant?.address} />
          </MKBox>
        </Grid>

        {/* Rental Preferences */}
        <Grid item xs={12} md={6}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Rental Preferences
            </MKTypography>

            <ReviewItem label="Location" value={formData.rental?.location} />
            <ReviewItem label="Property Type" value={formData.rental?.propertyType} />
            <ReviewItem label="Bedrooms" value={formData.rental?.bedrooms} />
            <ReviewItem label="Furnished" value={formData.rental?.furnished} />
            <ReviewItem label="Bathrooms" value={formData.rental?.bathrooms} />

            <ReviewItem label="Parking" value={formData.rental?.parking ? "Yes" : "No"} />

            <ReviewItem label="Pet Friendly" value={formData.rental?.petFriendly ? "Yes" : "No"} />

            <ReviewItem
              label="Monthly Budget"
              value={formData.rental?.budget ? `${formData.rental.budget} RWF` : null}
              highlight
            />

            <ReviewItem label="Move-in Date" value={formData.rental?.moveInDate} />
          </MKBox>
        </Grid>

        {/* Additional Property Requirements */}
        <Grid item xs={12}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Property Requirements
            </MKTypography>

            <ReviewItem
              label="Private Compound"
              value={formData.requirements?.privateCompound ? "Yes" : "No"}
            />

            <ReviewItem
              label="Security Included"
              value={formData.requirements?.security ? "Yes" : "No"}
            />

            <ReviewItem
              label="Internet Included"
              value={formData.requirements?.internet ? "Yes" : "No"}
            />

            <ReviewItem
              label="Garden / Outdoor Space"
              value={formData.requirements?.garden ? "Yes" : "No"}
            />

            <ReviewItem
              label="Near Transport"
              value={formData.requirements?.transport ? "Yes" : "No"}
            />

            <ReviewItem label="Other Requirements" value={formData.requirements?.other} />
          </MKBox>
        </Grid>

        {/* Rental Duration */}
        <Grid item xs={12}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Rental Duration & Notes
            </MKTypography>

            <ReviewItem label="Rental Duration" value={formData.duration?.duration} highlight />

            <ReviewItem label="Additional Notes" value={formData.duration?.other} />
          </MKBox>
        </Grid>
      </Grid>

      {/* Acknowledgment */}
      <MKBox
        mt={4}
        p={2}
        borderRadius="1rem"
        sx={{
          backgroundColor: "rgba(112,8,8,0.05)",
          border: "1px solid rgba(112,8,8,0.15)",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.acknowledgment || false}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  acknowledgment: e.target.checked,
                }))
              }
            />
          }
          label="I confirm the above information is correct and consent to be contacted regarding rental availability."
        />
      </MKBox>
    </MKBox>
  );
}

function House() {
  const [activeStep, setActiveStep] = useState(0);
  const [_status, setStatus] = useState("idle");
  const [_isLoading, setIsLoading] = useState(false);
  const [_isBooked, setIsBooked] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      const { fullName, phone, email, address } = formData.applicant;

      if (!fullName) newErrors.fullName = "Full name is required";
      if (!phone) newErrors.phone = "Phone number is required";
      if (!email) newErrors.email = "Email is required";
      if (!address) newErrors.address = "Location is required";
    }

    if (step === 1) {
      const r = formData.rental;

      if (!r.location?.trim()) {
        newErrors.location = "Preferred location is required";
      }

      if (!r.propertyType) {
        newErrors.propertyType = "Please select property type";
      }

      if (!r.bedrooms) {
        newErrors.bedrooms = "Please select number of bedrooms";
      }

      if (!r.furnished) {
        newErrors.furnished = "Please select furnished preference";
      }

      if (!r.budget?.trim()) {
        newErrors.budget = "Monthly budget is required";
      }

      if (!r.moveInDate) {
        newErrors.moveInDate = "Move-in date is required";
      }
    }
    if (step === 2) {
      if (!formData.requirements.other?.trim()) {
        newErrors.other = "Additional information is required";
      }
    }
    if (step === 3) {
      if (!formData.duration.duration) {
        newErrors.duration = "Please select rental duration";
      }

      if (!formData.duration.other?.trim()) {
        newErrors.other = "Additional information is required";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCloseModal = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenModal(false);
  };

  const steps = [
    "Applicant Info",
    "Rental Preferences",
    "Additional Requirements",
    "Rental Duration",
    "Review & Submit",
  ];

  const totalSteps = steps.length;
  const progress = ((activeStep + 1) / totalSteps) * 100;

  const handleNext = () => {
    const isValid = validateStep(activeStep);
    if (!isValid) return;

    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const [formData, setFormData] = useState({
    applicant: {
      fullName: "",
      phone: "",
      email: "",
      contactMethod: "",
      address: "",
    },
    rental: {
      location: "",
      propertyType: "",
      bedrooms: "",
      bathrooms: "",
      furnished: "",
      parking: false,
      petFriendly: false,
      budget: "",
      moveInDate: "",
    },
    requirements: {
      privateCompound: false,
      security: false,
      internet: false,
      garden: false,
      transport: false,
      other: "",
    },
    duration: {
      duration: "",
      other: "",
    },
    acknowledgment: false,
  });

  const buildPayload = (data) => ({
    applicant: {
      fullName: data.applicant.fullName,
      phone: data.applicant.phone,
      email: data.applicant.email,
      contactMethod: data.applicant.contactMethod,
      address: data.applicant.address,
    },

    rental: {
      location: data.rental.location,
      propertyType: data.rental.propertyType,
      bedrooms: data.rental.bedrooms,
      bathrooms: data.rental.bathrooms,
      furnished: data.rental.furnished,
      parking: data.rental.parking,
      petFriendly: data.rental.petFriendly,
      budget: data.rental.budget,
      moveInDate: data.rental.moveInDate,
    },

    requirements: {
      privateCompound: data.requirements.privateCompound,
      security: data.requirements.security,
      internet: data.requirements.internet,
      garden: data.requirements.garden,
      transport: data.requirements.transport,
      other: data.requirements.other,
    },

    duration: {
      period: data.duration.duration,
      notes: data.duration.other,
    },

    acknowledgment: data.acknowledgment,
    submittedAt: new Date().toISOString(),
  });

  const handleSubmit = async () => {
    const isValid = validateStep(activeStep);
    if (!isValid) return;
    setStatus("loading");
    setIsLoading(true);

    try {
      const payload = buildPayload(formData);

      const res = await axios.post(`${SERVER_URL}/book/house/`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.status === 200 && res.data === "Booked") {
        setAlertMsg("Thank you... we’ll get back to you soon");
        setStatus("success");
        setOpenModal(true);
        setTimeout(() => {
          navigate("/services/booking");
        }, 3000);
      } else {
        setStatus("error");
        alert("Booking failed. Please try again.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setStatus("error");
      alert("An error occurred while submitting your request.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <ApplicantInfoStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 1:
        return (
          <RentalPreferencesStep formData={formData} setFormData={setFormData} errors={errors} />
        );

      case 2:
        return (
          <RentalRequirementsStep formData={formData} setFormData={setFormData} errors={errors} />
        );

      case 3:
        return <RentalDurationStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 4:
        return (
          <HouseRentalReviewSubmitStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <>
        <DefaultNavbar
          routes={routes}
          action={{
            type: "internal",
            route: "/services/booking",
            label: "BOOK NOW",
            color: "info",
          }}
          transparent
          light
        />
        <MKBox
          minHeight="50vh"
          width="100%"
          sx={{
            backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
              `${linearGradient(
                rgba(gradients.dark.main, 0.6),
                rgba(gradients.dark.state, 0.6)
              )}, url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "grid",
            placeItems: "center",
            py: 10,
          }}
        >
          <Container>
            <Grid
              container
              item
              xs={12}
              lg={8}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{ mx: "auto", textAlign: "center" }}
            >
              <MKTypography
                variant="h2"
                color="white"
                mb={2}
                sx={({ breakpoints, typography: { size } }) => ({
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                })}
              >
                Discover Comfort and Convenience
              </MKTypography>
            </Grid>
          </Container>
        </MKBox>

        {/* Wrapper for all cards */}
        <MKBox
          display="flex"
          flexDirection="column"
          alignItems="stretch" // <-- important
          gap={6}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 900, md: 1100, lg: 1200 }, // responsive width
            px: { xs: 2, sm: 3, md: 0 }, // side padding on small screens
            mx: "auto",
            mt: -8,
          }}
        >
          {/* Stepper Card */}
          <Card
            sx={{
              width: "100%",
              p: { xs: 3, md: 5 },
              borderRadius: "2rem",
              background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.85))",
              backdropFilter: "blur(18px) saturate(160%)",
              boxShadow: "0 24px 48px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            {/* ================= MOBILE STEP INDICATOR ================= */}
            <MKBox display={{ xs: "block", md: "none" }}>
              <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MKTypography fontWeight={600} sx={{ color: "#700808" }}>
                  Step {activeStep + 1}
                </MKTypography>
                <MKTypography variant="body2" color="text.secondary">
                  {totalSteps} steps
                </MKTypography>
              </MKBox>

              {/* Progress Bar */}
              <MKBox
                sx={{
                  width: "100%",
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: "rgba(112,8,8,0.12)",
                  overflow: "hidden",
                  mb: 1.5,
                }}
              >
                <MKBox
                  sx={{
                    width: `${progress}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #700808, #9e0b0b)",
                    transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
                  }}
                />
              </MKBox>

              <MKTypography fontWeight={500}>{steps[activeStep]}</MKTypography>
            </MKBox>

            {/* ================= DESKTOP STEPPER ================= */}
            <MKBox display={{ xs: "none", md: "block" }} mb={4}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        "& .MuiStepIcon-root": {
                          color: "rgba(112,8,8,0.2)",
                          fontSize: "1.2rem",
                          transition: "all 0.3s ease",
                        },
                        "& .MuiStepIcon-root.Mui-active": {
                          color: "#700808",
                          fontSize: "1.4rem",
                        },
                        "& .MuiStepIcon-root.Mui-completed": {
                          color: "#700808",
                        },
                        "& .MuiStepLabel-label": {
                          fontSize: "0.85rem",
                          color: "rgba(0,0,0,0.5)",
                          mt: 1,
                        },
                        "& .MuiStepLabel-label.Mui-active": {
                          color: "#700808",
                          fontWeight: 600,
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </MKBox>

            {/* ================= STEP CONTENT ================= */}
            <MKBox mt={{ xs: 3, md: 2 }}>{renderStepContent(activeStep)}</MKBox>

            {/* ================= ACTION BUTTONS ================= */}
            <MKBox
              display="flex"
              justifyContent="space-between"
              mt={4}
              gap={2}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              {/* Back */}
              <MKButton
                fullWidth
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{
                  borderRadius: "1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#700808",
                  backgroundColor: "rgba(112,8,8,0.06)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(112,8,8,0.12)",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(0,0,0,0.05)",
                    color: "rgba(112,8,8,0.3)",
                  },
                }}
              >
                Back
              </MKButton>

              {/* Next / Submit */}
              <MKButton
                fullWidth
                onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                sx={{
                  borderRadius: "1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  background: "linear-gradient(90deg, #700808, #9e0b0b)",
                  color: "#fff",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 10px 20px rgba(112,8,8,0.35)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                }}
              >
                {activeStep === steps.length - 1 ? "Submit Request" : "Next"}
              </MKButton>
              {/* <MKButton
                fullWidth
                disabled
                sx={{
                  borderRadius: "1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  background: "#e0e0e0",
                  color: "#888",
                  cursor: "not-allowed",
                  boxShadow: "none",
                  "&:hover": {
                    background: "#e0e0e0",
                    boxShadow: "none",
                    transform: "none",
                  },
                  "&:active": {
                    transform: "none",
                  },
                }}
              >
                Coming Soon
              </MKButton> */}
            </MKBox>
          </Card>

          {/* Contact Card */}
          <Card
            sx={{
              width: "100%",
              p: { xs: 4, md: 6 },
              borderRadius: "2rem",
              background: "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.9))",
              backdropFilter: "blur(14px) saturate(160%)",
              boxShadow: "0 30px 60px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <MKBox display="flex" flexDirection="column" alignItems="center" gap={6}>
              {/* Header */}
              <MKBox textAlign="center">
                <MKTypography variant="h4" fontWeight={600} sx={{ letterSpacing: "-0.5px" }}>
                  Other Ways to Reach Us
                </MKTypography>
                <MKTypography variant="body2" color="text.secondary" mt={1}>
                  We’re always available through the channels below
                </MKTypography>
              </MKBox>

              {/* Contact Info */}
              <Grid container spacing={4} justifyContent="center">
                {[
                  {
                    title: "Phone",
                    icon: <PhoneIcon />,
                    items: ["(+250) 795 305 418", "(+250) 796 716 708", "(+250) 796 505 768"],
                  },
                  {
                    title: "Email",
                    icon: <EmailIcon />,
                    items: [
                      "info@helpereverywhere.com",
                      "helpereverywheremail@gmail.com",
                      "support@helpereverywhere.com",
                    ],
                  },
                ].map((block, idx) => (
                  <Grid item xs={12} md={4} key={idx}>
                    <MKBox
                      p={4}
                      borderRadius="1.75rem"
                      sx={{
                        backgroundColor: "#fff",
                        boxShadow: "0 16px 30px rgba(0,0,0,0.06)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 24px 40px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {/* Title */}
                      <MKBox display="flex" alignItems="center" gap={1.5} mb={3}>
                        <MKBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            backgroundColor: "rgba(112,8,8,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {React.cloneElement(block.icon, {
                            sx: { color: "#700808", fontSize: "1.4rem" },
                          })}
                        </MKBox>

                        <MKTypography variant="h6" fontWeight={600}>
                          {block.title}
                        </MKTypography>
                      </MKBox>

                      {/* Items */}
                      <MKBox display="flex" flexDirection="column" gap={1.5}>
                        {block.items.map((item, i) => (
                          <MKTypography
                            key={i}
                            sx={{
                              fontSize: "0.95rem",
                              fontWeight: 500,
                              color: "#700808",
                              backgroundColor: "rgba(112,8,8,0.05)",
                              px: 2,
                              py: 1,
                              borderRadius: "0.75rem",
                            }}
                          >
                            {item}
                          </MKTypography>
                        ))}
                      </MKBox>
                    </MKBox>
                  </Grid>
                ))}
              </Grid>

              {/* Social & Address */}
              <MKBox textAlign="center">
                <MKBox display="flex" justifyContent="center" gap={2} mb={2}>
                  {[
                    {
                      icon: <WhatsAppIcon />,
                      color: "#25D366",
                      link: "https://wa.me/250795305418",
                    },
                    {
                      icon: <FacebookIcon />,
                      color: "#1877F2",
                      link: "https://facebook.com/helpereverywhere",
                    },
                    {
                      icon: <InstagramIcon />,
                      color: "#E4405F",
                      link: "https://instagram.com/helpereverywhere",
                    },
                    {
                      icon: <TwitterIcon />,
                      color: "#1DA1F2",
                      link: "https://twitter.com/helpereverywhere",
                    },
                  ].map((social, idx) => (
                    <MKBox
                      key={idx}
                      component="a"
                      href={social.link}
                      target="_blank"
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "14px",
                        backgroundColor: "rgba(0,0,0,0.04)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: social.color,
                          transform: "translateY(-2px)",
                          "& svg": { color: "#fff" },
                        },
                      }}
                    >
                      {React.cloneElement(social.icon, {
                        sx: { fontSize: "1.5rem", color: social.color },
                      })}
                    </MKBox>
                  ))}
                </MKBox>

                <MKTypography variant="body2" color="text.secondary" fontWeight={500}>
                  KG 314 ST, Kigali, Rwanda
                </MKTypography>
              </MKBox>
            </MKBox>
          </Card>
        </MKBox>

        <MKBox pt={6} px={1} mt={6}>
          <DefaultFooter content={footerRoutes} />
        </MKBox>
        <Snackbar
          open={openModal}
          autoHideDuration={3000}
          onClose={handleCloseModal}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Alert
            onClose={handleCloseModal}
            severity="success"
            sx={{
              backgroundColor: "#7008084d",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "0.75rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            {alertMsg}
          </Alert>
        </Snackbar>
      </>
    </>
  );
}

export default House;
